import React, { useEffect, useMemo, useState } from 'react';
import { ShiftTimeContext } from '../model';
import { computeShiftTimelineModel } from '../utils';

interface ShiftTimelineProps {
    shiftTimeContext?: ShiftTimeContext;
}

// Blue used for the elapsed progress fill and the NOW marker; grey for the
// remaining track and the tick labels. Kept local since these are timeline
// chrome, not status colors.
const PROGRESS_COLOR = '#0073bb';
const TRACK_COLOR = '#e9ebed';
const LABEL_COLOR = '#5f6b7a';

const HALF_MINUTE_MS = 30 * 1000;
const REFRESH_MS = 30 * 1000;

/**
 * Horizontal shift timeline: hourly tick labels across the top, a "NOW" marker,
 * and a progress bar filled from shift start up to the current warehouse time.
 *
 * The current time is derived from the viewer's own clock (Meridian has no
 * timeline primitive, so this uses native elements). We compute a one-time
 * offset from `serverTime` to correct for device-clock skew and tick locally.
 * Renders nothing when no usable shift window is available.
 */
const ShiftTimeline: React.FC<ShiftTimelineProps> = ({ shiftTimeContext }) => {
    // One-time skew between the server clock and this device's clock, so the
    // NOW marker stays correct even if the viewer's clock is off.
    const skewMs = useMemo(() => {
        const serverTime = shiftTimeContext?.serverTime;
        if (!serverTime) return 0;
        const serverMs = new Date(serverTime).getTime();
        if (Number.isNaN(serverMs)) return 0;
        const skew = serverMs - Date.now();
        // Ignore tiny skews (sub-30s) — not worth correcting for.
        return Math.abs(skew) < HALF_MINUTE_MS ? 0 : skew;
    }, [shiftTimeContext?.serverTime]);

    const [nowMs, setNowMs] = useState(() => Date.now() + skewMs);

    // Re-tick every 30s so the marker and fill advance live during the shift.
    useEffect(() => {
        setNowMs(Date.now() + skewMs);
        const id = setInterval(() => setNowMs(Date.now() + skewMs), REFRESH_MS);
        return () => clearInterval(id);
    }, [skewMs]);

    const model = useMemo(
        () => computeShiftTimelineModel(shiftTimeContext, nowMs),
        [shiftTimeContext, nowMs]
    );

    if (!model) return null;

    return (
        <div data-testid="shift-timeline" style={{ width: '100%' }}>
            {/* Tick labels + NOW marker */}
            <div style={{ position: 'relative', height: 20, borderTop: `1px solid ${TRACK_COLOR}`, paddingTop: 4 }}>
                {model.ticks.map((tick, i) => (
                    <span
                        key={`${tick.label}-${i}`}
                        style={{
                            position: 'absolute',
                            left: `${tick.positionPercent}%`,
                            transform:
                                i === 0
                                    ? 'translateX(0)'
                                    : i === model.ticks.length - 1
                                    ? 'translateX(-100%)'
                                    : 'translateX(-50%)',
                            fontSize: 12,
                            color: LABEL_COLOR,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {tick.label}
                    </span>
                ))}
                {model.nowPositionPercent != null && (
                    <span
                        data-testid="shift-timeline-now"
                        style={{
                            position: 'absolute',
                            left: `${model.nowPositionPercent}%`,
                            transform: 'translateX(-50%)',
                            top: 0,
                            backgroundColor: PROGRESS_COLOR,
                            color: '#ffffff',
                            fontSize: 11,
                            fontWeight: 700,
                            lineHeight: '16px',
                            padding: '0 6px',
                            borderRadius: 4,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        NOW
                    </span>
                )}
            </div>

            {/* Progress bar */}
            <div
                style={{
                    position: 'relative',
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: TRACK_COLOR,
                    marginTop: 6,
                    overflow: 'hidden',
                }}
            >
                <div
                    data-testid="shift-timeline-fill"
                    style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        height: '100%',
                        width: `${model.progressPercent}%`,
                        backgroundColor: PROGRESS_COLOR,
                        borderRadius: 3,
                    }}
                />
            </div>
        </div>
    );
};

export default ShiftTimeline;
